import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApolloError } from '@apollo/client';
import { Subscription } from 'rxjs';

import {
	apolloErrorContains,
	nonRetryableErrorSubStrings,
	SubscriptionManager,
	SubscriptionManagerConfigCreate
} from '@shure/cloud/shared/apollo';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { FirmwareUpdateRequestSubscriptionGQL, InventoryDeviceFragment } from './graphql/generated/cloud-sys-api';

export interface DeviceFirmwareRequestIds {
	primary?: string;
	hosted?: string;
}

export interface FirmwareUpdateRequestActions {
	requestIdsToRegister: string[];
	requestIdsToDeregister: string[];
}

@Injectable({ providedIn: 'root' })
export class SysApiFirmwareUpdateRequestManagementService {
	private readonly logger: ILogger;
	private readonly firmwareUpdateRequestsMap = new Map<string, DeviceFirmwareRequestIds>();

	// A "map" to map a device nodeId to the current primary & hosted FirmwareUpdateRequests
	// This is necessary because we need to subscribe/unsubscribe to individual firmwareUpdate
	// request as they come and go on a per-device basis.
	private readonly fwUpdateSubscriptionMgr = new SubscriptionManager({
		subscriptionType: 'inventory-devices',
		create: (config): Subscription => this.createFirmwareUpdateSubscription(config),
		retryWaitMs: 10_000,
		maxRetryAttempts: 3
	});

	constructor(
		logger: ILogger,
		private destroyRef: DestroyRef,
		private readonly firmwareUpdateRequestSubscriptionGQL: FirmwareUpdateRequestSubscriptionGQL
	) {
		this.logger = logger.createScopedLogger('FirmwareUpdateRequestMgmtService');
	}

	public processDeviceChanged(inventoryFragment: InventoryDeviceFragment): void {
		const deviceNodeId = inventoryFragment.id;
		const newPrimaryRequestId = inventoryFragment.features.firmwareUpdateRequests?.primary.current?.id;
		const newHostedRequestId = inventoryFragment.features.firmwareUpdateRequests?.hosted.current?.id;

		const { requestIdsToRegister, requestIdsToDeregister } = this.getChangedFirmwareRequestIds(
			deviceNodeId,
			newPrimaryRequestId,
			newHostedRequestId
		);

		for (const requestId of requestIdsToDeregister) {
			this.fwUpdateSubscriptionMgr.deregister(requestId);
		}

		if (requestIdsToRegister.length > 0) {
			this.fwUpdateSubscriptionMgr.register(requestIdsToRegister);
		}
	}

	//
	// Method to cleanup the firmwareUpdateRequestsMap and unsubscribe to any firmwareUpdateRequests
	// for a given device. Presumably when a device is removed.
	//
	public processDeviceRemoved(deviceNodeId: string): void {
		const requestIdsToDeregister = this.getCurrentFirmwareRequestIds(deviceNodeId);

		for (const requestId of requestIdsToDeregister) {
			this.fwUpdateSubscriptionMgr.deregister(requestId);
		}
	}

	private getChangedFirmwareRequestIds(
		deviceNodeId: string,
		newPrimaryRequestId: string | null | undefined,
		newHostedRequestId: string | null | undefined
	): FirmwareUpdateRequestActions {
		const currentMapEntry = this.firmwareUpdateRequestsMap.get(deviceNodeId);
		const updatedMapEntry: DeviceFirmwareRequestIds = {};
		const requestIdsToRegister: string[] = [];
		const requestIdsToDeregister: string[] = [];

		const normalizedPrimaryRequestId = this.normalizeRequestId(newPrimaryRequestId);
		const normalizedHostedRequestId = this.normalizeRequestId(newHostedRequestId);
		const currentPrimaryRequestId = currentMapEntry?.primary;
		const currentHostedRequestId = currentMapEntry?.hosted;

		// if there WAS a primary requestID, and it's changed we need to deregister it.
		if (currentPrimaryRequestId && currentPrimaryRequestId !== normalizedPrimaryRequestId) {
			requestIdsToDeregister.push(currentPrimaryRequestId);
		}

		// if there WAS a hosted requestID, and it's changed we need to deregister it.
		if (currentHostedRequestId && currentHostedRequestId !== normalizedHostedRequestId) {
			requestIdsToDeregister.push(currentHostedRequestId);
		}

		// If there IS a primary requestID, make sure it's registered
		if (normalizedPrimaryRequestId !== undefined) {
			updatedMapEntry.primary = normalizedPrimaryRequestId;
			requestIdsToRegister.push(normalizedPrimaryRequestId);
		}

		// If there IS a hosted requestID, make sure it's registered
		if (normalizedHostedRequestId !== undefined) {
			updatedMapEntry.hosted = normalizedHostedRequestId;
			requestIdsToRegister.push(normalizedHostedRequestId);
		}

		if (updatedMapEntry.primary || updatedMapEntry.hosted) {
			this.firmwareUpdateRequestsMap.set(deviceNodeId, updatedMapEntry);
		} else {
			this.firmwareUpdateRequestsMap.delete(deviceNodeId);
		}

		return { requestIdsToRegister, requestIdsToDeregister };
	}

	private getCurrentFirmwareRequestIds(deviceNodeId: string): string[] {
		const mapEntry = this.firmwareUpdateRequestsMap.get(deviceNodeId);
		const requestIdsToDeregister: string[] = [];

		if (mapEntry?.primary) {
			requestIdsToDeregister.push(mapEntry.primary);
		}

		if (mapEntry?.hosted) {
			requestIdsToDeregister.push(mapEntry.hosted);
		}

		this.firmwareUpdateRequestsMap.delete(deviceNodeId);

		return requestIdsToDeregister;
	}

	private normalizeRequestId(requestId: string | null | undefined): string | undefined {
		if (requestId === null || requestId === undefined || requestId.length === 0) {
			return undefined;
		}
		return requestId;
	}

	private createFirmwareUpdateSubscription({ id, retryCallback }: SubscriptionManagerConfigCreate): Subscription {
		return this.firmwareUpdateRequestSubscriptionGQL
			.subscribe(
				{
					id: id
				},
				{
					errorPolicy: 'ignore',
					fetchPolicy: 'network-only'
				}
			)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					// No explicit handling of messages here. We rely on the apollo cache triggering
					// the watch query on the per-device subscription.
				},
				complete: () => {
					this.logger.error(
						'firmwareUpdateRequestSubscription',
						'subscription completed. Will retry.',
						JSON.stringify({ nodeId: id })
					);
					retryCallback();
				},
				error: (error: ApolloError) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'firmwareUpdateRequestSubscriptionGQL',
						'Encountered retryable error',
						JSON.stringify({ id, error }, null, 3)
					);
					retryCallback();
				}
			});
	}
}
